/**
 * useHrActor — pulls the current Clerk user's identity into the
 * envelope-context fields every HR-initiate form must POST.
 *
 * Closes audit finding C1 from
 * docs/audits/m30_5_completeness_audit_2026_05_23.md:
 *
 *   The M30.5.3 + M30.5.4 forms hardcoded
 *   actor_user_id = "user_hr_director_e2e" and
 *   actor_role = "hr_director" as literal strings. Every envelope
 *   row written through those forms in production would carry the
 *   test sentinel instead of the real HR person's identity —
 *   commandment 39 violation. The H1 envelope writer's
 *   EnvelopeContextMissing 422 only catches EMPTY user_id, not a
 *   non-empty wrong one.
 *
 * Returns:
 *   - actorUserId: Clerk user.id (the canonical primary key)
 *   - actorRole: from publicMetadata.role if set; falls back to
 *                "hr_user" (the conservative role that satisfies the
 *                backend's require_role >= hr_user gate on the
 *                /hr-initiate routes — actual route-level enforcement
 *                is still server-side via JWT, not body-side)
 *   - isReady: true when actorUserId is non-empty AND Clerk is loaded
 *
 * Use isReady to disable the form's submit button so HR can't click
 * before Clerk has resolved their session. The backend's H1
 * EnvelopeContextMissing 422 is a fail-safe; this hook is the
 * fail-fast.
 */

import { useUser } from "@clerk/clerk-react";

export interface HrActor {
  actorUserId: string;
  actorRole: string;
  isReady: boolean;
}

export function useHrActor(): HrActor {
  const { user, isLoaded } = useUser();
  const actorUserId = user?.id ?? "";
  const metaRole = user?.publicMetadata?.role;
  const actorRole =
    typeof metaRole === "string" && metaRole.length > 0
      ? metaRole
      : "hr_user";
  return {
    actorUserId,
    actorRole,
    isReady: isLoaded && actorUserId.length > 0,
  };
}
